import './component.scss';
import '@stimulus/polyfills';
import { Application, Controller } from 'stimulus';
import { PrefectureModalController } from './controllers/prefecture_modal_controller';
import { CityModalController } from './controllers/city_modal_controller';
import { SearchApiController } from './controllers/search_api_controller';

import './images/prefecture_icon.svg';
import './images/close_icon.svg';
import './images/construction_icon.svg';
import './images/left_arrow_icon.svg';
import './images/city_icon.png';
import './images/right_arrow_icon.svg';
import './images/search_icon.svg';

const app = Application.start();

class SearchPanelController extends Controller {
  static targets = [
    'prefectureModal',
    'cityModal',
    'prefectureToggle',
    'modalClose',
    'prefectureButton',
    'prefectureConfirm',
    'prefectureConfirmButton',
    'prefectureNoResultsMessage',
    'cityInputBox',
    'cityInputText',
    'cityConfirm',
    'cityNoResultsMessage',
    'cityButtonsContainer',
    'prefectureName',
    'noResultsMessage',
    'submitNoResultsMessage',
    'prefectureInputBox',
    'prefectureInputText',
    'categoryHidden',
    'categoryVisible',
    'partTypeCheckbox',
    'citySelectedButton',
    'cityChangeButton',
    'cityArrowIcon'
  ];

  static values = {
    form: Object
  };

  connect() {
    // コントローラーを初期化
    this.prefectureModalController = new PrefectureModalController(this);
    this.cityModalController = new CityModalController(this);
    this.searchApiController = new SearchApiController(this);

    // data属性から初期値が設定されていない場合のデフォルト値を設定
    if (!this.formValue || Object.keys(this.formValue).length === 0) {
      this.formValue = {
        prefecture_id: null,
        prefecture_name: null,
        city_id: null,
        city_name: null,
        categories: [],
        part_types: []
      };
    }
    
    // 表示状態の初期化
    this.initializeDisplayState();
  }

  // 表示状態の初期化
  initializeDisplayState() {
    // 市区町村ボックスのアクティブ状態を設定
    if (this.formValue.city_id) {
      if (this.hasCityInputBoxTarget) {
        this.cityInputBoxTarget.classList.add('subsidies-search-panel__input-box--active');
      }
    }
    
    // 表示テキストを更新
    this.cityModalController.updateCityDisplay();
  }

  // 都道府県選択モーダル関連のメソッド
  openPrefectureModal(event) {
    this.prefectureModalController.openPrefectureModal(event);
  }

  selectPrefecture(event) {
    this.prefectureModalController.selectPrefecture(event);
    this.cityModalController.updateCityDisplay();
  }

  confirmPrefectureSelection(event) {
    this.prefectureModalController.confirmPrefectureSelection(event);
  }

  selectCity(event) {
    this.prefectureModalController.selectCity(event);
  }

  confirmCitySelection(event) {
    this.prefectureModalController.confirmCitySelection(event);
  }

  // 市区町村選択モーダル関連のメソッド
  openCityModal(event) {
    this.cityModalController.openCityModal(event);
  }

  selectCity(event) {
    this.cityModalController.selectCity(event);
  }

  confirmCitySelection(event) {
    this.cityModalController.confirmCitySelection(event);
  }

  // 市区町村選択をクリアする
  async clearCity(event) {
    // モーダルが開かないようにイベント伝播を止める
    event.stopPropagation();
    event.preventDefault();

    // formValueをクリア
    this.formValue = {
      ...this.formValue,
      city_id: null,
      city_name: null
    };

    // 隠しフィールドをクリア
    const cityInput = this.element.querySelector('input[name="city_id"]');
    if (cityInput) {
      cityInput.value = '';
    }

    // input-boxのアクティブ状態を解除
    if (this.hasCityInputBoxTarget) {
      this.cityInputBoxTarget.classList.remove('subsidies-search-panel__input-box--active');
    }

    // 表示を更新（ピル型ボタンを削除し、placeholderを表示）
    if (this.hasCityInputTextTarget) {
      this.cityInputTextTarget.innerHTML = '<span class="subsidies-search-panel__city-placeholder">指定なし</span>';
    }

    // 変更ボタンと矢印アイコンを再表示
    if (this.hasCityChangeButtonTarget) {
      this.cityChangeButtonTarget.style.display = '';
    }
    if (this.hasCityArrowIconTarget) {
      this.cityArrowIconTarget.style.display = '';
    }

    // 検索結果件数を再取得
    const submitButton = this.element.querySelector('.subsidies-search-panel__submit-button');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.value = '検索中...';
      submitButton.style.opacity = '0.5';
    }

    try {
      const count = await this.searchResultsCount();
      this.updateSubmitButton(count);
    } catch (error) {
      this.updateSubmitButton(1);
    }
  }

  async togglePartType(event) {
    const checkbox = event.currentTarget;
    
    if (checkbox.checked) {
      if (!this.formValue.part_types.includes(checkbox.value)) {
        this.formValue = {
          ...this.formValue,
          part_types: [...this.formValue.part_types, checkbox.value]
        };
      }
    } else {
      this.formValue = {
        ...this.formValue,
        part_types: this.formValue.part_types.filter(type => type !== checkbox.value)
      };
    }

    const submitButton = this.element.querySelector('.subsidies-search-panel__submit-button');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.value = '検索中...';
      submitButton.style.opacity = '0.5';
    }

    try {
      const count = await this.searchResultsCount();
      this.updateSubmitButton(count);
    } catch (error) {
      this.updateSubmitButton(1);
    }
  }

  // 検索API関連のメソッド
  searchResultsCount() {
    return this.searchApiController.searchResultsCount();
  }

  updateSubmitButton(count) {
    this.searchApiController.updateSubmitButton(count);
  }

  // モーダルボタン状態更新（共通メソッド）
  async updateModalButtonState(confirmButton, messageTarget, originalText) {
    confirmButton.disabled = true;
    confirmButton.textContent = '検索中...';
    confirmButton.style.opacity = '0.5';

    try {
      const count = await this.searchApiController.getSearchResultsCount();
      
      if (count > 0) {
        confirmButton.disabled = false;
        confirmButton.textContent = originalText;
        confirmButton.style.opacity = '';
        if (messageTarget) messageTarget.style.display = 'none';
      } else {
        confirmButton.disabled = true;
        confirmButton.textContent = originalText;
        confirmButton.style.opacity = '';
        if (messageTarget) messageTarget.style.display = 'block';
      }
      
      this.searchApiController.updateSubmitButton(count);
    } catch (error) {
      confirmButton.disabled = false;
      confirmButton.textContent = originalText;
      confirmButton.style.opacity = '';
      if (messageTarget) messageTarget.style.display = 'none';
      this.searchApiController.updateSubmitButton(1);
    }
  }

  // モーダルを閉じる
  closeModal(event) {
    event.preventDefault();
    this.closeAllModals();
  }

  closeAllModals() {
    const modals = [this.prefectureModalTarget, this.cityModalTarget].filter(modal => modal);
    modals.forEach(modal => {
      modal.classList.remove('is-active');
    });
  }
}

app.register('refosumu-subsidies-search-panel', SearchPanelController);
